import React, { useState, useRef, useEffect } from "react";
import "../styles/CameraModal.css";

const CameraModalWrapper = ({ isOpen, onClose, onCapture }) => {
   const [stream, setStream] = useState(null);
   const [error, setError] = useState("");
   const [photo, setPhoto] = useState(null);
   const videoRef = useRef(null);
   const canvasRef = useRef(null);

   useEffect(() => {
      if (isOpen) {
         setPhoto(null); // Clear any previous photo when opening
         startCamera();
      } else {
         stopCamera();
      }
      // Cleanup on unmount
      return () => stopCamera();
   }, [isOpen]);

   const startCamera = async () => {
      setError("");
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
         try {
            const stream = await navigator.mediaDevices.getUserMedia({
               video: { facingMode: "environment" },
            });
            setStream(stream);
            if (videoRef.current) {
               videoRef.current.srcObject = stream;
            }
         } catch (err) {
            console.error("Error accessing camera:", err);
            setError(
               "Could not access camera. Please ensure you've given permission and are not using another app with the camera. If you are on a device without a camera, please use a different device."
            );
         }
      } else {
         setError(
            "Your browser does not support camera access. Please use a different browser or device."
         );
      }
   };

   const stopCamera = () => {
      if (stream) {
         stream.getTracks().forEach((track) => track.stop());
         setStream(null);
      }
   };

   const handleCapture = () => {
      if (videoRef.current && canvasRef.current) {
         const video = videoRef.current;
         const canvas = canvasRef.current;
         canvas.width = video.videoWidth;
         canvas.height = video.videoHeight;
         const context = canvas.getContext("2d");
         context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

         const dataUrl = canvas.toDataURL("image/jpeg");
         setPhoto(dataUrl);
      }
   };

   const retakePhoto = () => {
      setPhoto(null);
      // Restart camera when retaking photo
      if (stream) {
         stream.getTracks().forEach((track) => track.stop());
         setStream(null);
      }
      startCamera();
   };

   const usePhoto = () => {
      if (photo) {
         // Convert data URL to blob
         fetch(photo)
            .then((res) => res.blob())
            .then((blob) => {
               onCapture(blob);
               onClose();
            })
            .catch((error) => {
               console.error("Error converting photo:", error);
            });
      }
   };

   if (!isOpen) return null;

   return (
      <div className="camera-modal-overlay">
         <div className="camera-modal-content">
            <div className="camera-modal-header">
               <h2>Take a Photo</h2>
               <button onClick={onClose} className="close-button">
                  &times;
               </button>
            </div>
            <div className="camera-modal-body">
               {error ? (
                  <div className="camera-error">{error}</div>
               ) : photo ? (
                  <div className="camera-preview">
                     <img
                        src={photo}
                        alt="Captured"
                        style={{
                           maxWidth: "100%",
                           maxHeight: "400px",
                           borderRadius: "8px",
                        }}
                     />
                  </div>
               ) : (
                  <div className="camera-feed">
                     <video ref={videoRef} autoPlay playsInline muted />
                     <canvas ref={canvasRef} style={{ display: "none" }} />
                  </div>
               )}
            </div>
            <div className="camera-modal-footer">
               {!photo ? (
                  <button
                     onClick={handleCapture}
                     disabled={!stream || error}
                     className="capture-button"
                  >
                     <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        width="24"
                        height="24"
                     >
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                        />
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth="2"
                           d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                     </svg>
                     Capture Photo
                  </button>
               ) : (
                  <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
                     <button onClick={retakePhoto} className="retake-button">
                        <svg
                           xmlns="http://www.w3.org/2000/svg"
                           fill="none"
                           viewBox="0 0 24 24"
                           stroke="currentColor"
                           width="20"
                           height="20"
                        >
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                           />
                        </svg>
                        Retake
                     </button>
                     <button onClick={usePhoto} className="use-photo-button">
                        <svg
                           xmlns="http://www.w3.org/2000/svg"
                           fill="none"
                           viewBox="0 0 24 24"
                           stroke="currentColor"
                           width="20"
                           height="20"
                        >
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 13l4 4L19 7"
                           />
                        </svg>
                        Use Photo
                     </button>
                  </div>
               )}
            </div>
         </div>
      </div>
   );
};

export default CameraModalWrapper;
